import React, { useContext } from 'react';
import {
  BannerEntity,
  ConvertedProduct,
  DiscoveryProduct,
  OptionConvertProductCart,
  ProductResponse,
  DiscoveryPromotion,
} from '../types';
import { useDetailPage } from '../hooks';
import { ProductCardProps as ProductCardPropsDesktop } from '@teko/ecommerce';
import { ProductCardProps as ProductCardPropsMobile } from '@teko/ecommerce/mobile';
import { CartContextState } from '../contexts';
import { productHelper as defaultProductHelper } from 'helpers';
import { ProductPriceProps } from '@teko/teko-ecommerce';

interface ProductHelper {
  convertProductDetail?: (prod: DiscoveryProduct) => ConvertedProduct;
  convertProductCartDesktop?: (
    prod: DiscoveryProduct,
    option?: OptionConvertProductCart,
  ) => ProductCardPropsDesktop;
  convertProductCartMobile?: (
    prod: DiscoveryProduct,
    frames?: BannerEntity[],
    cart?: CartContextState,
  ) => ProductCardPropsMobile;
  getLinkProduct?: (prod: DiscoveryProduct, skuVariant?: string) => string;
}

export interface ProductDetailState {
  product?: ConvertedProduct;
  primitiveProduct?: DiscoveryProduct;
  productNotes: string[];
  frame?: string;
  onSelectVariant: (variant: any, index: number) => void;
  stockAvailable?: number;
  showStock?: boolean;
  isOutOfStock?: boolean;
  loadingSameBrandProducts?: boolean;
  sameBrandProducts: DiscoveryProduct[];
  hasShippingAndWarningNote?: boolean;
  isCanAddToCard?: boolean;
  loading?: boolean;
  productHelper: ProductHelper;
  showFlashSale?: boolean;

  selectedPromotion?: DiscoveryPromotion;
  setSelectedPromotion?: (arg: DiscoveryPromotion) => void;
  price?: ProductPriceProps;
  setPrice?: (arg: ProductPriceProps) => void;
  selectedPrice?: number | null;
  setSelectedPrice?: (arg: number | null) => void;
}

export interface SameBrandProductConfig {
  categories?: string[];
}

export interface DetailProvider {
  children: React.ReactNode;
  serverProduct: ProductResponse;
  productHelper?: ProductHelper;
  sameBrandProductConfig?: SameBrandProductConfig;
}

const initialState: ProductDetailState = {
  productNotes: [],
  onSelectVariant: () => {},
  sameBrandProducts: [],
  productHelper: {},
};

const ProductDetailStateContext =
  React.createContext<ProductDetailState>(initialState);

export const ProductDetailProvider: React.FunctionComponent<DetailProvider> = ({
  children,
  serverProduct,
  productHelper,
  sameBrandProductConfig,
}: DetailProvider) => {
  productHelper = productHelper || {
    convertProductDetail: defaultProductHelper.convertProductDetail,
    convertProductCartDesktop: defaultProductHelper.convertProductCard,
    getLinkProduct: defaultProductHelper.getLinkProduct,
  };
  const detailPage = useDetailPage(
    serverProduct,
    productHelper?.convertProductDetail,
    productHelper?.getLinkProduct,
    sameBrandProductConfig,
  );

  return (
    <>
      <ProductDetailStateContext.Provider
        value={{ ...detailPage, productHelper }}
      >
        {children}
      </ProductDetailStateContext.Provider>
    </>
  );
};

export function useProductDetailState(): ProductDetailState {
  const context = useContext(ProductDetailStateContext);
  if (!context) {
    throw new Error(
      'useProductDetailState must be inside a ProductDetailStateContext with a state value',
    );
  }
  return context;
}
